/**
 * The 2026 DOOH Pre-Flight mockup as a template document. Fifth and last of the
 * seeded templates.
 *
 * Pre-flight is a *projection*, not a measurement, and almost everything distinctive
 * about this template follows from that:
 *
 *   - **There is no delivery.** Actuals render `TBD`, never `0`. That is the DOOH
 *     mockup's own `ACTUAL IMPRESSIONS: TBD`, and it is why the data model insists an
 *     unknowable metric is `null` rather than zero — a pre-flight report claiming zero
 *     impressions reads as a campaign that failed.
 *   - **The placement list is capped at the top 100** by estimated audience (D42),
 *     because an uncapped geo-targeted list against a 500,000-screen inventory (F20)
 *     runs to roughly seventy pages of screens that may never play anything.
 *   - **The cap must be visible.** The table discloses "showing 100 of N", the ranking
 *     basis is stated, and `VenueTypeSummary` reports counts across *all* matching
 *     placements. A capped list presented as complete is a misrepresentation, and
 *     these three together are what stop it being one.
 *
 * Mockup defects deliberately not reproduced (scope §4.3): the source omits page
 * numbers on pages 1–2 then labels pages `3 of 6` and `4 of 6` in a four-page file, and
 * alternates the title between `Digital Out of Home-Pre-Flight` and
 * `Digital Out of Home - Pre-Flight`. Page numbering is engine-computed and the title
 * is authored once.
 */

import type { Template } from './template-schema.js';

export const DOOH_PREFLIGHT_TEMPLATE: Template = {
  schemaVersion: 1,
  id: 'white-label-default-dooh-preflight',
  name: 'V2 — DOOH Pre-Flight',
  version: 1,
  status: 'draft',
  agencyId: null,
  channels: ['DOOH'],
  lifecycle: ['PREFLIGHT'],

  theme: {
    color: {
      primary: '#001345', accent: '#06CDF2', surface: '#FFFFFF', surfaceMuted: '#F5F5F5',
      border: '#E8E8E8', textPrimary: '#0A0A0A', textInverse: '#FFFFFF',
      headerFrom: '#000A1E', headerTo: '#00174B',
      page: '#F1F3F6', stripBorder: '#2B5FA8',
    },
    typography: { fontFamily: 'Roboto', baseSizePt: 11, scale: 1.21 },
    page: { widthIn: 8.5, heightIn: 11, marginIn: 0 },
    branding: { companyName: 'ProData Media', copyrightName: 'ProData Media' },
    iconStyle: 'v2',
    layout: { pageBackground: 'waves' },
  },

  chrome: {
    header: [{
      id: 'hdr', type: 'PageHeader',
      // Authored once, so the two spellings in the source file cannot both exist.
      props: { title: 'Digital Out of Home - Pre-Flight', showAccentRule: true },
      bindings: { clientName: 'campaign.clientName', campaignId: 'campaign.reportSo', logo: 'presentation.logo', showLogo: 'presentation.showLogo' },
    }],
    footer: [{
      id: 'ftr', type: 'PageFooter', props: {},
      // The copyright names the AGENCY (D: reports carry the agency's copyright, not the client's).
      bindings: { companyName: 'campaign.agencyName', year: '$substring(report.periodEnd, 0, 4)' },
    }],
  },

  pages: [
    // ------------------------------------------------------------- page 1 --
    {
      id: 'summary',
      mode: 'fixed',
      title: 'Campaign plan',
      blocks: [
        {
          /*
           * Creative LEFT, details RIGHT — the same order as display, audio and CTV (review, 2026-10-28).
           * The original mockup led with the details panel; the V2 set reads the same way across channels.
           */
          id: 'identity-row', type: 'Columns', props: { columns: 2 }, bindings: {},
          children: [
            {
              id: 'creative', type: 'CreativePreview',
              props: { maxHeightIn: 1.9, caption: '* Representative of creative used in campaign.', hideWhenEmpty: true },
              bindings: { assetId: 'campaign.bannerAssetId' },
            },
            {
              id: 'meta', type: 'CampaignMetaCard',
              props: {
                variant: 'labels',
                // The billboard mark on the name row, so it is not a second "#" beside Campaign ID.
                nameIcon: 'channel-dooh',
                /*
                 * The pre-flight caveat, carried in the panel with the dates it
                 * qualifies. A master list of POTENTIAL screens is not a promise that
                 * every one of them will play the creative, and the mockup says so
                 * where a reader is already looking at the plan.
                 */
                note: 'This is the pre-flight report which shows the geographic targeting selected and a master list of potential screen placements. This does not guarantee creatives are displayed on all screen locations listed below.',
              },
              bindings: {
                clientName: 'campaign.clientName', campaignName: 'campaign.name',
                startDate: 'campaign.startDate',
                endDate: 'campaign.endDate', campaignId: 'campaign.reportSo',
              },
            },
          ],
        },
        {
          /*
           * The geo summary is a CARD above the contracted pair, not a trailing note.
           *
           * It answers "where was this bought" and the mockup gives it that prominence;
           * as loose text under the map it read as a caption on the map instead.
           */
          id: 'geo-card', type: 'SectionCard',
          props: { title: 'Geographic Targeting', icon: 'dooh-geo-targeting' }, bindings: {},
          children: [{
            id: 'geo-summary', type: 'RichTextBlock', props: { size: 'lead' },
            bindings: {
              // Name the buy at its own granularity: a city / county / DMA buy reads its place names, a
              // state buy its state name(s), a ZIP-radius buy "25 mile radius @ 21201" (the mockup's case),
              // a plain ZIP buy its ZIPs. Whichever the campaign actually targets.
              html: `$count(targeting.cities) > 0 ? $join(targeting.cities, ", ")
                : $count(targeting.countyLabels) > 0 ? $join(targeting.countyLabels, ", ")
                : $count(targeting.dmas) > 0 ? $join(targeting.dmas, ", ")
                : $count(targeting.states) > 0 ? $join(targeting.states, ", ")
                : $count(targeting.zips) > 0
                  ? (targeting.radiusMiles ? ($string(targeting.radiusMiles) & " mile radius @ " & $join(targeting.zips, ", ")) : $join(targeting.zips, ", "))
                : "Targeting not specified"`,
            },
          }],
        },
        {
          // The mockup's headline pair. `actual` is null pre-flight, and the manifest's
          // unavailableRendering: 'tbd' turns that into "TBD" rather than "0".
          id: 'contracted-actual', type: 'ContractedVsActual',
          props: { contractedIcon: 'dooh-contracted-impressions', actualIcon: 'dooh-actual-impressions' },
          bindings: { contracted: 'overall.contract.targetImpressions', actual: 'dooh.actualImpressions' },
        },
        {
          id: 'placement-map-card', type: 'SectionCard',
          props: { title: 'Potential Screen Locations', icon: 'pin' }, bindings: {},
          children: [{
            // 2.7in (was 3.25): an agency logo makes the header band taller, and at 3.25in the FBI-branded
            // NCS-DOOH-0926 overflowed this fixed page by 32px — a hard render error, so no PDF at all.
            id: 'pin-map', type: 'PinMap', props: { heightIn: 2.7 },
            bindings: { mapAssetId: 'dooh.mapAssetId' },
          }],
        },
      ],
    },

    // ------------------------------------------------------------- page 2+ --
    // The venue mix and the placement list on ONE flowing page. The venue summary used to sit alone on a
    // fixed page (about 85% empty); the list now follows it directly and splits across sheets with a
    // repeating header — the mockup's venue table spans several pages for exactly this reason.
    {
      id: 'placements',
      mode: 'flowing',
      title: 'Venue mix and projected placements',
      blocks: [
        {
          id: 'venue-summary-card', type: 'SectionCard',
          props: { title: 'All Matching Placements by Venue Type' }, bindings: {},
          children: [{
            // Counts across EVERY matching placement, including the ones the table
            // below cannot show. This is the completeness claim the capped list
            // cannot make on its own (D42).
            id: 'venue-summary', type: 'VenueTypeSummary', props: { columns: 3 },
            bindings: { rows: 'dooh.venueSummary', total: 'dooh.totalPotentialVenues' },
          }],
        },
        {
          id: 'cap-note', type: 'RichTextBlock', props: {},
          bindings: {
            // The ranking basis, stated. A cap whose basis is invisible is indistinguishable from an
            // arbitrary truncation. The basis is stored as a field name ("venueType"); spell it out in
            // words ("venue type") rather than print the internal name to a client.
            html: `"The placement list below shows the top "
                   & $string($count(dooh.potentialVenues)) & " of "
                   & $formatNumber(dooh.totalPotentialVenues, "#,##0")
                   & " matching placements, ranked by "
                   & $lowercase($replace(dooh.potentialVenuesRankedBy, /([a-z])([A-Z])/, "$1 $2"))
                   & ". Counts above cover all matching placements."`,
          },
        },
        {
          id: 'placements-card', type: 'SectionCard',
          props: { title: 'Projected Placement List' }, bindings: {},
          children: [{
            id: 'placements-table', type: 'PaginatedTable',
            props: {
              // ID | Venue Type | Address. No "Venue Name" column: the screen inventory carries no real
              // venue names — that field held the category again ("Convenience Stores / Convenience
              // Stores"). Its width goes to the address, which now wraps at word boundaries instead of
              // mid-word ("Huntsvill e", "AL 3580 5"). No Est. Audience column — the live inventory
              // carries no per-screen audience, so a column of blanks or zeros would mislead.
              columns: JSON.stringify([
                { key: 'screenId', label: 'ID', widthPct: 11 },
                { key: 'venueType', label: 'Venue Type', widthPct: 31 },
                { key: 'address', label: 'Address', widthPct: 58 },
              ]),
              zebra: true,
              // Belt and braces: the data model already caps `potentialVenues` at 100,
              // so this bound can never be the binding one. Both are deliberate — the
              // model stops an over-long list being *stored*, this stops one being
              // *rendered* if the model ever loosens.
              maxRows: 100,
              showingOf: true,
            },
            bindings: {
              rows: `dooh.potentialVenues.{
                "screenId": screenId,
                "venueType": venueType,
                "address": addressLine & (addressLine and cityStateZip ? ", " : "") & cityStateZip
              }`,
              // The "of N" figure is the FULL population, not the stored array's
              // length — otherwise the disclosure would read "showing 100 of 100".
              totalRows: 'dooh.totalPotentialVenues',
            },
          }],
        },
      ],
    },
  ],

  createdAt: '2026-08-16T00:00:00.000Z',
  createdBy: 'm5-dooh-preflight',
};
